import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PalmettoButton, PalmettoIcon, PalmettoModal, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import type { RichTextNode } from 'types';
import {
  ChecklistConditionName,
  ChecklistConditionOperator,
  ChecklistEvaluationKey,
  ChecklistItemInputType,
  ChecklistReferenceKey,
} from 'types';
import type { EvaluationParams } from 'types';

import { ChecklistItemCard } from './ChecklistItemCard';
import { ChecklistItemEditForm } from './ChecklistItemEditForm';
import { serializeParamsRaw } from './paramsRaw';

export interface SectionFormValue {
  key: string;
  title: string;
  order: number;
}

export interface InputFormValue {
  key: string;
  label: string;
  type: ChecklistItemInputType;
  required?: boolean;
  options?: Array<{
    label: string;
    value: string;
    decisionType?: string;
    requirementFlagSettingId?: string;
    flagMappings?: Array<{ requirementFlagSettingId: string; criterionId?: string; problemId?: string }>;
  }>;
  referenceKey?: ChecklistReferenceKey;
  requirementFlagSettingId?: string;
  conditions?: Array<{ name: string; operator?: string; params?: string[] }>;
  tooltipText?: RichTextNode[];
  evaluationKey?: ChecklistEvaluationKey;
  evaluationParams?: EvaluationParams;
}

export interface TemplateItemFormValue {
  key: string;
  name: string;
  description: string;
  tooltipText?: RichTextNode[];
  order: number;
  sectionKey?: string;
  conditions?: Array<{
    name: string;
    operator?: ChecklistConditionOperator;
    params?: string[];
    _paramsRaw?: string;
  }>;
  inputs?: InputFormValue[];
  documentTypes?: string[];
  _stableId?: string;
}

export interface ChecklistTemplateItemsEditorProps {
  items: TemplateItemFormValue[];
  onChange: (items: TemplateItemFormValue[]) => void;
  programTypes: string[];
  sections?: SectionFormValue[];
  onSectionsChange?: (sections: SectionFormValue[]) => void;
  onEditingChange?: (isEditing: boolean) => void;
}

export const getStableId = (item: TemplateItemFormValue): string => {
  if (!item._stableId) {
    item._stableId = crypto.randomUUID();
  }
  return item._stableId;
};

const newItemIndex = -1;
const sectionIdPrefix = 'section::';

interface SortableSectionContainerProps {
  section: SectionFormValue;
  isEditingAny: boolean;
  onAddItem: (sectionKey: string) => void;
  onRemoveSection: (sectionKey: string) => void;
  children: React.ReactNode;
}

const SortableSectionContainer: FC<SortableSectionContainerProps> = ({
  section,
  isEditingAny,
  onAddItem,
  onRemoveSection,
  children,
}) => {
  const sortable = useSortable({ id: `${sectionIdPrefix}${section.key}`, disabled: isEditingAny });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };

  const handleAdd = useCallback(() => onAddItem(section.key), [onAddItem, section.key]);
  const handleRemove = useCallback(() => onRemoveSection(section.key), [onRemoveSection, section.key]);

  return (
    <div ref={sortable.setNodeRef} style={style} className="mb-16 rounded-[6px] bg-canvas-highlight-default p-16">
      <div className="mb-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          {!isEditingAny && (
            <div
              {...sortable.attributes}
              {...sortable.listeners}
              className="shrink-0 rotate-90 cursor-grab touch-none"
            >
              <PalmettoIcon name="dragReorderDots" size="md" />
            </div>
          )}
          <span className="body-bold-400 text-text-primary">{section.title}</span>
        </div>
        <div className="flex items-center gap-8">
          <PalmettoButton
            type="button"
            emphasis="tertiary"
            size="small"
            layout="hug"
            leadingIcon={<PalmettoIcon name="add" size="xs" />}
            onClick={handleAdd}
            disabled={isEditingAny}
          >
            Add Item
          </PalmettoButton>
          <button
            type="button"
            onClick={handleRemove}
            className="cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary"
            aria-label={`Remove section ${section.title}`}
          >
            <PalmettoIcon name="trash" size="sm" />
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-16">{children}</div>
    </div>
  );
};

export const ChecklistTemplateItemsEditor = ({
  items,
  onChange,
  programTypes,
  sections = [],
  onSectionsChange,
  onEditingChange,
}: ChecklistTemplateItemsEditorProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    onEditingChange?.(editingIndex !== null);
  }, [editingIndex, onEditingChange]);

  const [newItemSectionKey, setNewItemSectionKey] = useState<string | undefined>(undefined);
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  // A raw useState-driven value re-rendered inside PalmettoModal on every keystroke causes
  // Ark UI's FocusTrap to reactivate and reset focus to the modal's first tabbable element
  // (the close button) instead of the input. react-hook-form's Controller isolates re-renders
  // to just the changed field, so the modal itself never re-renders while typing.
  const {
    control: addSectionControl,
    handleSubmit: handleAddSectionSubmit,
    reset: resetAddSectionForm,
  } = useForm<{ title: string }>({ defaultValues: { title: '' } });
  const [confirmDialog, setConfirmDialog] = useState<
    { kind: 'item'; itemKey: string } | { kind: 'section'; sectionKey: string } | null
  >(null);
  const prevProgramTypesRef = useRef(programTypes);
  // This writes `items` back through `onChange`, so it would feed itself if it reran on its own
  // output. It can't: every run reassigns prevProgramTypesRef first, so a rerun triggered by the
  // resulting `items` change sees `programTypes === prev` and bails before touching anything.
  // Only a new program-type selection does work.
  useEffect(() => {
    const prev = prevProgramTypesRef.current;
    prevProgramTypesRef.current = programTypes;
    if (programTypes === prev || programTypes.length === 0) return;

    let changed = false;
    const updatedItems = items.map((item) => {
      if (!item.conditions) return item;
      const updatedConditions = item.conditions.map((c) => {
        if (c.name !== ChecklistConditionName.programType || !c.params) return c;
        const validParams = c.params.filter((p) => programTypes.includes(p));
        if (validParams.length !== c.params.length) {
          changed = true;
          const paramsRaw = c._paramsRaw ? serializeParamsRaw(validParams) : undefined;
          return { ...c, params: validParams.length > 0 ? validParams : undefined, _paramsRaw: paramsRaw };
        }
        return c;
      });
      return changed ? { ...item, conditions: updatedConditions } : item;
    });
    if (changed) {
      onChange(updatedItems);
    }
  }, [programTypes, items, onChange]);

  const sortedItems = useMemo(() => [...items].toSorted((a, b) => a.order - b.order), [items]);
  const sortedSections = useMemo(() => [...sections].toSorted((a, b) => a.order - b.order), [sections]);

  const sectionGroups = useMemo(() => {
    const sectionKeySet = new Set(sortedSections.map((s) => s.key));
    const grouped = new Map<string, TemplateItemFormValue[]>();
    const ungrouped: TemplateItemFormValue[] = [];

    for (const item of sortedItems) {
      if (item.sectionKey && sectionKeySet.has(item.sectionKey)) {
        if (!grouped.has(item.sectionKey)) grouped.set(item.sectionKey, []);
        grouped.get(item.sectionKey)!.push(item);
      } else {
        ungrouped.push(item);
      }
    }

    return {
      ungrouped,
      sections: sortedSections.map((s) => ({ section: s, items: grouped.get(s.key) ?? [] })),
    };
  }, [sortedItems, sortedSections]);

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const keyboardSensor = useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates });
  const allSensors = useSensors(pointerSensor, keyboardSensor);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      if (activeId.startsWith(sectionIdPrefix) && overId.startsWith(sectionIdPrefix)) {
        const activeSectionKey = activeId.slice(sectionIdPrefix.length);
        const overSectionKey = overId.slice(sectionIdPrefix.length);
        const oldIndex = sections.findIndex((s) => s.key === activeSectionKey);
        const newIndex = sections.findIndex((s) => s.key === overSectionKey);
        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(sections, oldIndex, newIndex);
          onSectionsChange?.(reordered.map((s, idx) => ({ ...s, order: idx + 1 })));
        }
      } else if (!activeId.startsWith(sectionIdPrefix) && !overId.startsWith(sectionIdPrefix)) {
        const oldIndex = items.findIndex((item) => getStableId(item) === activeId);
        const newIndex = items.findIndex((item) => getStableId(item) === overId);
        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedItems = arrayMove(items, oldIndex, newIndex);
          onChange(reorderedItems.map((item, idx) => Object.assign({}, item, { order: idx + 1 })));
        }
      }
    },
    [items, sections, onChange, onSectionsChange],
  );

  const handleAddItem = useCallback((sectionKey?: string) => {
    setNewItemSectionKey(sectionKey);
    setEditingIndex(newItemIndex);
  }, []);

  const handleEditItem = useCallback((index: number) => {
    setEditingIndex(index);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setNewItemSectionKey(undefined);
  }, []);

  const handleSaveItem = useCallback(
    (savedItem: TemplateItemFormValue) => {
      if (editingIndex === newItemIndex) {
        const newItem: TemplateItemFormValue = {
          ...savedItem,
          order: items.length + 1,
          sectionKey: newItemSectionKey,
          _stableId: savedItem._stableId || crypto.randomUUID(),
        };
        onChange([...items, newItem]);
      } else if (editingIndex !== null) {
        const updatedItems = [...items];
        updatedItems[editingIndex] = {
          ...savedItem,
          order: items[editingIndex].order,
          sectionKey: items[editingIndex].sectionKey,
        };
        onChange(updatedItems);
      }
      setEditingIndex(null);
      setNewItemSectionKey(undefined);
    },
    [editingIndex, newItemSectionKey, items, onChange],
  );

  const handleRequestRemoveItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;
      setConfirmDialog({ kind: 'item', itemKey: item.key });
    },
    [items],
  );

  const performRemoveItem = useCallback(
    (itemKey: string) => {
      const removedIndex = items.findIndex((item) => item.key === itemKey);
      if (removedIndex === -1) return;
      const filteredItems = items.filter((item) => item.key !== itemKey);
      const renumberedItems = filteredItems.map((item, idx) => Object.assign({}, item, { order: idx + 1 }));
      onChange(renumberedItems);
      if (editingIndex === removedIndex) {
        setEditingIndex(null);
      }
    },
    [items, onChange, editingIndex],
  );

  const submitAddSection = useCallback(
    (data: { title: string }) => {
      if (!onSectionsChange) return;
      const newSection: SectionFormValue = {
        key: `section-${crypto.randomUUID()}`,
        title: data.title.trim(),
        order: sections.length + 1,
      };
      onSectionsChange([...sections, newSection]);
      resetAddSectionForm();
      setIsAddSectionOpen(false);
    },
    [sections, onSectionsChange, resetAddSectionForm],
  );

  const handleAddSection = useCallback(() => {
    void handleAddSectionSubmit(submitAddSection)();
  }, [handleAddSectionSubmit, submitAddSection]);

  const handleOpenAddSection = useCallback(() => {
    resetAddSectionForm();
    setIsAddSectionOpen(true);
  }, [resetAddSectionForm]);

  const handleCancelAddSection = useCallback(() => {
    setIsAddSectionOpen(false);
    resetAddSectionForm();
  }, [resetAddSectionForm]);

  const handleRequestRemoveSection = useCallback(
    (sectionKey: string) => {
      if (!onSectionsChange) return;
      const hasItems = items.some((i) => i.sectionKey === sectionKey);
      if (hasItems) {
        toast.error('Remove all items from this section before deleting it.');
        return;
      }
      setConfirmDialog({ kind: 'section', sectionKey });
    },
    [items, onSectionsChange],
  );

  const performRemoveSection = useCallback(
    (sectionKey: string) => {
      if (!onSectionsChange) return;
      const filtered = sections.filter((s) => s.key !== sectionKey).map((s, idx) => ({ ...s, order: idx + 1 }));
      onSectionsChange(filtered);
      const updatedItems = items.map((item) =>
        item.sectionKey === sectionKey ? { ...item, sectionKey: undefined } : item,
      );
      onChange(updatedItems);
    },
    [sections, onSectionsChange, items, onChange],
  );

  const handleConfirmDialogCancel = useCallback(() => {
    setConfirmDialog(null);
  }, []);

  const handleConfirmDialogConfirm = useCallback(() => {
    if (!confirmDialog) return;
    if (confirmDialog.kind === 'item') {
      performRemoveItem(confirmDialog.itemKey);
    } else {
      performRemoveSection(confirmDialog.sectionKey);
    }
    setConfirmDialog(null);
  }, [confirmDialog, performRemoveItem, performRemoveSection]);

  const handleNewSectionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddSection();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelAddSection();
      }
    },
    [handleAddSection, handleCancelAddSection],
  );

  const isAddingNew = editingIndex === newItemIndex;
  const isEditingAny = editingIndex !== null;

  const renderItem = (item: TemplateItemFormValue, contained: boolean) => {
    const index = items.findIndex((i) => getStableId(i) === getStableId(item));
    return (
      <ChecklistItemCard
        key={getStableId(item)}
        item={item}
        contained={contained}
        isEditing={editingIndex === index}
        disableInteractions={isEditingAny}
        onEdit={() => handleEditItem(index)}
        onRemove={() => handleRequestRemoveItem(index)}
        onSave={handleSaveItem}
        onCancelEdit={handleCancelEdit}
        programTypes={programTypes}
      />
    );
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-left heading-200 text-text-primary">Checklist Items</h2>
        {onSectionsChange && (
          <PalmettoButton
            type="button"
            emphasis="secondary"
            size="small"
            layout="hug"
            leadingIcon={<PalmettoIcon name="add" size="xs" />}
            onClick={handleOpenAddSection}
          >
            Section
          </PalmettoButton>
        )}
      </div>

      <DndContext sensors={allSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={[
            ...sectionGroups.ungrouped.map(getStableId),
            ...sectionGroups.sections.flatMap(({ section, items: si }) => [
              `${sectionIdPrefix}${section.key}`,
              ...si.map(getStableId),
            ]),
          ]}
          strategy={verticalListSortingStrategy}
        >
          {sectionGroups.ungrouped.map((item) => renderItem(item, false))}

          {sectionGroups.sections.map(({ section, items: sectionItems }) => (
            <SortableSectionContainer
              key={section.key}
              section={section}
              isEditingAny={isEditingAny}
              onAddItem={handleAddItem}
              onRemoveSection={handleRequestRemoveSection}
            >
              {sectionItems.map((item) => renderItem(item, true))}
              {isAddingNew && newItemSectionKey === section.key && (
                <div className="mb-4 rounded-[6px] border-2 border-input-border-default bg-canvas-subtle p-16">
                  <ChecklistItemEditForm
                    item={null}
                    onSave={handleSaveItem}
                    onCancel={handleCancelEdit}
                    programTypes={programTypes}
                  />
                </div>
              )}
              {sectionItems.length === 0 && !(isAddingNew && newItemSectionKey === section.key) && (
                <div className="border-separator rounded-[6px] border border-dashed p-8 text-center text-sm text-text-secondary">
                  No items in this section
                </div>
              )}
            </SortableSectionContainer>
          ))}
        </SortableContext>
      </DndContext>

      {items.length === 0 && sections.length === 0 && !isAddingNew && (
        <div className="border-separator mb-4 rounded-[6px] border border-dashed p-6 text-center text-text-secondary">
          Add a section to get started.
        </div>
      )}

      {onSectionsChange && isAddSectionOpen && (
        <PalmettoModal
          open={isAddSectionOpen}
          onClose={handleCancelAddSection}
          invertedBackground={false}
          bottomContent={
            <div className="flex justify-end gap-8">
              <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleCancelAddSection}>
                Cancel
              </PalmettoButton>
              <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleAddSection}>
                Add
              </PalmettoButton>
            </div>
          }
        >
          <div className="flex flex-col gap-16">
            <h2>Add Section</h2>
            <Controller
              name="title"
              control={addSectionControl}
              rules={{ required: 'Section title is required.' }}
              render={({ field, fieldState }) => (
                <PalmettoTextInput
                  id="new-section-title"
                  label="Section title"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  onKeyDown={handleNewSectionKeyDown}
                  validationMessage={
                    fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                  }
                />
              )}
            />
          </div>
        </PalmettoModal>
      )}

      {confirmDialog && (
        <PalmettoModal
          open
          onClose={handleConfirmDialogCancel}
          invertedBackground={false}
          bottomContent={
            <div className="flex justify-end gap-8">
              <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleConfirmDialogCancel}>
                Cancel
              </PalmettoButton>
              <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleConfirmDialogConfirm}>
                Delete
              </PalmettoButton>
            </div>
          }
        >
          <div className="flex flex-col gap-8">
            <h2>{confirmDialog.kind === 'item' ? 'Delete this checklist item?' : 'Delete this section?'}</h2>
            <p className="text-text-secondary">This action cannot be undone.</p>
          </div>
        </PalmettoModal>
      )}
    </div>
  );
};
